import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { AlertCircle, CheckCircle2, Info, Loader2, RotateCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { CHANNEL_STATE_LABEL, type ChannelState } from "@/lib/connections";

// Peças visuais compartilhadas pelos cards da tela de Conexões.

export const COUNTRY_CODES = [
  { code: "+55", label: "BR +55" },
  { code: "+1", label: "US +1" },
  { code: "+351", label: "PT +351" },
  { code: "+244", label: "AO +244" },
  { code: "+258", label: "MZ +258" },
  { code: "+238", label: "CV +238" },
  { code: "+239", label: "ST +239" },
  { code: "+245", label: "GW +245" },
] as const;

const STATE_BADGE: Record<ChannelState, "success" | "gold" | "muted" | "loss"> = {
  connected: "success",
  connecting: "gold",
  disconnected: "muted",
  error: "loss",
};

export function ChannelStatusBadge({ state }: { state: ChannelState }): JSX.Element {
  return (
    <Badge variant={STATE_BADGE[state]} className="ml-auto whitespace-nowrap" aria-live="polite">
      {state === "connecting" && <Loader2 size={11} className="animate-spin" aria-hidden="true" />}
      {CHANNEL_STATE_LABEL[state]}
    </Badge>
  );
}

interface CardShellProps {
  id: string;
  icon: LucideIcon;
  title: string;
  badge: ReactNode;
  children: ReactNode;
}

export function CardShell({ id, icon: Icon, title, badge, children }: CardShellProps): JSX.Element {
  return (
    <section className="rounded-xl border border-border bg-card overflow-hidden" aria-labelledby={id}>
      <div className="flex items-center gap-2.5 px-5 py-4 border-b border-border bg-[hsl(var(--surface-raised)/0.4)]">
        <Icon size={14} className="text-muted-foreground" aria-hidden="true" />
        <h2 id={id} className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          {title}
        </h2>
        {badge}
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </section>
  );
}

type NoticeTone = "success" | "danger" | "warning" | "info";

const NOTICE_STYLE: Record<NoticeTone, string> = {
  success: "border-primary/25 bg-primary/10 text-primary",
  danger: "border-destructive/30 bg-destructive/10 text-destructive",
  warning: "border-[hsl(var(--gold)/0.3)] bg-[hsl(var(--gold)/0.07)] text-foreground",
  info: "border-border bg-[hsl(var(--surface-raised)/0.6)] text-muted-foreground",
};

const NOTICE_ICON: Record<NoticeTone, LucideIcon> = {
  success: CheckCircle2,
  danger: AlertCircle,
  warning: AlertCircle,
  info: Info,
};

export function Notice({ tone, children }: { tone: NoticeTone; children: ReactNode }): JSX.Element {
  const Icon = NOTICE_ICON[tone];
  return (
    <div
      role={tone === "danger" ? "alert" : "status"}
      className={cn("flex items-start gap-2 rounded-lg border px-3 py-2.5 text-sm", NOTICE_STYLE[tone])}
    >
      <Icon
        size={15}
        className={cn("mt-0.5 shrink-0", tone === "warning" && "text-[hsl(var(--gold))]")}
        aria-hidden="true"
      />
      <div className="min-w-0 leading-snug">{children}</div>
    </div>
  );
}

export function StepList({ steps }: { steps: readonly ReactNode[] }): JSX.Element {
  return (
    <ol className="space-y-2.5">
      {steps.map((step, index) => (
        <li key={index} className="flex items-start gap-2.5 text-sm text-muted-foreground">
          <span className="mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 font-mono text-[10px] font-bold text-primary">
            {index + 1}
          </span>
          <span className="leading-snug">{step}</span>
        </li>
      ))}
    </ol>
  );
}

interface PhoneFieldProps {
  idPrefix: string;
  countryCode: string;
  phone: string;
  onCountryChange: (value: string) => void;
  onPhoneChange: (value: string) => void;
  disabled?: boolean;
}

export function PhoneField(props: PhoneFieldProps): JSX.Element {
  const { idPrefix, countryCode, phone, onCountryChange, onPhoneChange, disabled } = props;
  return (
    <div className="grid gap-3 sm:grid-cols-[auto_1fr] sm:items-end">
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-country`} variant="field">
          País
        </Label>
        <select
          id={`${idPrefix}-country`}
          value={countryCode}
          disabled={disabled}
          onChange={(e) => onCountryChange(e.target.value)}
          className="flex h-9 w-full rounded-md bg-[hsl(var(--input))] border border-[hsl(var(--border))] px-3 py-2 text-sm text-foreground transition-colors hover:border-[hsl(var(--border-strong))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {COUNTRY_CODES.map((cc) => (
            <option key={cc.code} value={cc.code}>
              {cc.label}
            </option>
          ))}
        </select>
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-phone`} variant="field">
          Telefone
        </Label>
        <Input
          id={`${idPrefix}-phone`}
          value={phone}
          disabled={disabled}
          onChange={(e) => onPhoneChange(e.target.value)}
          placeholder="(11) 99999-9999"
          inputMode="tel"
          autoComplete="tel-national"
        />
      </div>
    </div>
  );
}

/** Spinner com texto para "o bot está processando". */
export function Processing({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
      <Loader2 size={15} className="animate-spin text-primary" aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}

/** Falha ao carregar o status: nunca cai no estado "desconectado" (o usuário
 *  poderia iniciar uma conexão por cima de uma sessão ativa). */
export function LoadError({ onRetry, retrying }: { onRetry: () => void; retrying: boolean }): JSX.Element {
  return (
    <div className="space-y-3">
      <Notice tone="danger">Não foi possível carregar o status desta conexão.</Notice>
      <Button type="button" variant="outline" size="sm" onClick={onRetry} disabled={retrying}>
        <RotateCw size={14} className={retrying ? "animate-spin" : undefined} />
        Tentar de novo
      </Button>
    </div>
  );
}
